import React, { useEffect, useRef, useState } from "react";

const formatDateTime = (date) => {
  if (!date) return "";

  const d = date.toDate ? date.toDate() : new Date(date);

  return d.toLocaleString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ChatWindow = ({
  selectedChat,
  firebase,
  userMap = {},
}) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const currentEmail = firebase.auth.currentUser.email;

  const otherEmail = selectedChat
    ? selectedChat.providerId === currentEmail
      ? selectedChat.userId
      : selectedChat.providerId
    : "";

  const otherUser = userMap[otherEmail] ?? {
    email: otherEmail,
    name: otherEmail,
    headshotURL: "",
  };

  const otherName = otherUser.name;

  useEffect(() => {
    if (!selectedChat) {
      setMessages([]);
      return;
    }

    const unsubscribe = firebase.db
      .collection("inmail")
      .doc(selectedChat.id)
      .collection("messages")
      .orderBy("createdAt", "asc")
      .onSnapshot((snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setMessages(data);
      });

    return () => unsubscribe();
  }, [selectedChat, firebase]);

  useEffect(() => {
    if (!selectedChat?.id) return;

    const resetUnreadCount = async () => {
      try {
        const updateData =
          selectedChat.providerId === currentEmail
            ? { unreadCountProvider: 0 }
            : { unreadCountUser: 0 };

        await firebase.db
          .collection("inmail")
          .doc(selectedChat.id)
          .update(updateData);
      } catch (err) {
        console.error(
          "Failed to reset unread count:",
          err
        );
      }
    };

    resetUnreadCount();
  }, [selectedChat, currentEmail, firebase]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages]);

  const sendMessage = async () => {
    const message = text.trim();

    if (!message || !selectedChat) return;

    try {
      const now = new Date();

      await firebase.db
        .collection("inmail")
        .doc(selectedChat.id)
        .collection("messages")
        .add({
          text: message,
          senderId: currentEmail,
          createdAt: now,
        });

      const updateData = {
        lastMessage: message,
        lastMessageAt: now,
      };

      if (
        selectedChat.providerId === currentEmail
      ) {
        updateData.unreadCountUser =
          (selectedChat.unreadCountUser || 0) + 1;
      } else {
        updateData.unreadCountProvider =
          (selectedChat.unreadCountProvider || 0) + 1;
      }

      await firebase.db
        .collection("inmail")
        .doc(selectedChat.id)
        .update(updateData);

      setText("");
    } catch (err) {
      console.error(err);
    }
  };

  if (!selectedChat) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "#666",
        }}
      >
        Select a chat
      </div>
    );
  }

  return (
    <div
      style={{
        height: "90%",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {otherUser.headshotURL ? (
          <img
            src={otherUser.headshotURL}
            alt={otherName}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              objectFit: "cover",
              display:"block",
              float:"left",
              margin:"10px"
            }}
          />
        ) : (
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background: "#0078ff",
              color: "#fff",
              display:"block",
              float:"left",
              fontWeight: "600",
              margin:"10px"
            }}
          >
            {otherName
              ?.charAt(0)
              ?.toUpperCase()}
          </div>
        )}
        
        <div
          style={{
            display:"block",
              float:"left",
            fontWeight: "600",
            fontSize: "16px",
              margin:"10px"
          }}
        >
          {otherName}
        </div>
      </div>

      {/* MESSAGES */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
        }}
      >
        {messages.map((msg) => {
          const isMine =
            msg.senderId === currentEmail;

          return (
            <div
              key={msg.id}
              style={{
                display: "flex",
                justifyContent: isMine
                  ? "flex-end"
                  : "flex-start",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isMine
                    ? "flex-end"
                    : "flex-start",
                  maxWidth: "65%",
                }}
              >
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: "14px",
                    background: isMine
                      ? "#d9ecff"
                      : "#f1f1f1",
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {msg.text}
                </div>

                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "11px",
                    color: "#888",
                  }}
                >
                  {formatDateTime(
                    msg.createdAt
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {/* INPUT */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          padding: "12px",
          borderTop: "1px solid #e0e0e0",
          flexShrink: 0,
        }}
      >
        <input
          value={text}
          onChange={(e) =>
            setText(e.target.value)
          }
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              !e.shiftKey
            ) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder="Type a message..."
          style={{
            flex: 1,
            padding: "10px 12px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            outline: "none",
          }}
        />

        <button
          onClick={sendMessage}
          style={{
            padding: "10px 18px",
            border: "none",
            borderRadius: "8px",
            background: "#0078ff",
            color: "#fff",
            cursor: "pointer",
            fontWeight: "600",
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default ChatWindow;